import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function ContactFormDialog({ open, onClose, onSubmit, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    is_primary: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {

      // eslint-disable-next-line
      setForm({
        name: initial.name || "",
        email: initial.email || "",
        phone: initial.phone || "",
        role: initial.role || "",
        is_primary: !!initial.is_primary,
      });
    } else {
      setForm({ name: "", email: "", phone: "", role: "", is_primary: false });
    }
  }, [initial, open]);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Edit Contact" : "Add Contact"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            People associated with this customer — multiple emails and phones supported.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
            <Input
              data-testid="contact-form-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Full name"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Role / Title
            </Label>
            <Input
              data-testid="contact-form-role"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              placeholder="e.g. CTO, Procurement Lead"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
            <Input
              data-testid="contact-form-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Phone</Label>
            <Input
              data-testid="contact-form-phone"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="mt-1.5"
            />
          </div>
          <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
            <Checkbox
              data-testid="contact-form-primary"
              checked={form.is_primary}
              onCheckedChange={(v) => setForm((f) => ({ ...f, is_primary: !!v }))}
            />
            <span className="text-sm text-slate-700">Set as primary contact</span>
          </label>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="contact-form-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            data-testid="contact-form-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add contact"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
